import { Box, Button, Card, CardContent, Container, Divider, Grid2 as Grid, Paper, TextField, Typography } from '@mui/material'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useEffect, useState } from 'react'

import { userReadonlyAtom, usersAtom, usersByIdsAtomFamily } from '../domains/user/AtomUsers'
import { userUpsertOneCommandAtom } from '../domains/user/UserCommand'

/**
 * 单个用户显示组件
 * 使用 userReadonlyAtom 来获取单个用户数据
 */
function SingleUserDisplay({ userId }) {
  const [user] = useAtom(userReadonlyAtom(userId))

  console.log('---------------Render SingleUserDisplay userId---------------')

  // useEffect(() => {
  //   console.log('user subscribe, changed userId =>', userId)
  //   console.log('user subscribe, changed user =>', user)
  // }, [user, userId])

  if (!user) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography color="text.secondary">用户不存在 (ID: {userId})</Typography>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {user.firstName} {user.lastName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          ID: {user.resourceId}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          邮箱: {user.email}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          运动: {user.sports?.join(', ') || '无'}
        </Typography>
        {user.instructorCertificates?.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            证书: {user.instructorCertificates.join(', ')}
          </Typography>
        )}
        {user.bio && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {user.bio}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * 多个用户显示组件
 * 使用 usersByIdsAtomFamily 来获取多个用户数据
 */
function MultipleUsersDisplay({ userIds }) {
  const users = useAtomValue(usersByIdsAtomFamily(userIds))

  useEffect(() => {
    console.log('should run this once for MultipleUsersDisplay<<<===========1')
  }, [])

  // useEffect(() => {
  //   console.log('users by ids subscribe, changed userId =>', userIds)
  //   console.log('users by ids subscribe, changed users =>', users)
  // }, [userIds, users])

  // console.log('---------------Render MultipleUsersDisplay userIds---------------')

  if (users.length === 0) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography color="text.secondary">没有找到用户</Typography>
        </CardContent>
      </Card>
    )
  }

  return (
    <Grid container spacing={2}>
      {users.map((user) => (
        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={user.resourceId}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {user.firstName} {user.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ID: {user.resourceId}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                邮箱: {user.email}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  )
}

/**
 * 所有用户显示组件
 * 使用 usersAtom 来获取所有用户数据
 */
function AllUsersDisplay() {
  console.log('---------------Render AllUsersDisplay---------------')
  const [users] = useAtom(usersAtom)

  const userList = Object.values(users)

  if (userList.length === 0) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography color="text.secondary">暂无用户数据</Typography>
        </CardContent>
      </Card>
    )
  }

  return (
    <Grid container spacing={2}>
      {userList.map((user) => (
        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={user.resourceId}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {user.firstName} {user.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ID: {user.resourceId}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                邮箱: {user.email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                运动: {user.sports?.join(', ') || '无'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  )
}

/**
 * 生成默认表单数据
 * 每次调用都会生成新的唯一 ID
 */
const getDefaultFormData = () => ({
  resourceId: `user-1234`,
  firstName: 'John',
  lastName: 'Doe',
  email: 'john.doe@example.com',
  sports: 'SKI, SNOWBOARD',
  bio: 'Passionate about skiing and outdoor activities.',
  instructorCertificates: 'CSIA Level 1, CASI Level 2',
})

/**
 * 用户表单组件
 * 用于添加或更新用户数据到 usersAtom
 */
function UserForm() {
  const upsertUser = useSetAtom(userUpsertOneCommandAtom)
  // 默认占位符数据，可以直接提交
  const [formData, setFormData] = useState(getDefaultFormData())

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }))
  }

  const handleSubmit = (event) => {
    event.preventDefault()

    if (!formData.resourceId || !formData.firstName || !formData.lastName) {
      alert('请填写必填字段：ID、名字、姓氏')
      return
    }

    const newUser = {
      resourceId: formData.resourceId,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email || '',
      sports: formData.sports ? formData.sports.split(',').map((s) => s.trim().toUpperCase()) : [],
      bio: formData.bio || '',
      instructorCertificates: formData.instructorCertificates ? formData.instructorCertificates.split(',').map((c) => c.trim()) : [],
      certificateVerified: false,
      avatar: null,
    }

    upsertUser(newUser)

    // 重置表单为默认值（使用新的时间戳生成唯一 ID）
    setFormData(getDefaultFormData())
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          添加新用户
        </Typography>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                required
                label="用户 ID"
                value={formData.resourceId}
                onChange={handleChange('resourceId')}
                placeholder="user-001"
                helperText="必填，例如: user-001"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth required label="名字" value={formData.firstName} onChange={handleChange('firstName')} placeholder="John" />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth required label="姓氏" value={formData.lastName} onChange={handleChange('lastName')} placeholder="Doe" />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="邮箱" type="email" value={formData.email} onChange={handleChange('email')} placeholder="john.doe@example.com" />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="运动 (用逗号分隔)"
                value={formData.sports}
                onChange={handleChange('sports')}
                placeholder="SKI, SNOWBOARD"
                helperText="例如: SKI, SNOWBOARD"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="证书 (用逗号分隔)"
                value={formData.instructorCertificates}
                onChange={handleChange('instructorCertificates')}
                placeholder="CSIA Level 1, CASI Level 2"
                helperText="例如: CSIA Level 1, CASI Level 2"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="简介"
                value={formData.bio}
                onChange={handleChange('bio')}
                placeholder="Passionate about skiing and outdoor activities."
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Button type="submit" variant="contained" fullWidth>
                添加用户
              </Button>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  )
}

/**
 * 主开发演示组件
 * 展示如何使用不同的 Atom 来管理用户状态
 */
export default function AtomDevDemo() {
  const [selectedUserId, setSelectedUserId] = useState('1')
  const [selectedUserIds, setSelectedUserIds] = useState(['instructor-999', '1', '2', '3'])

  console.log('---------------Render AtomDevDemo---------------')
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Atom 开发演示
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        这个页面展示了如何使用 Jotai Atom 来管理用户状态。包括 usersAtom、userReadonlyAtom 和 usersByIdsAtomFamily 的使用。
      </Typography>

      <Divider sx={{ my: 4 }} />

      {/* 用户表单 */}
      <Box sx={{ mb: 4 }}>
        <UserForm />
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* 单个用户显示 - 使用 userReadonlyAtom */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          单个用户显示 (userReadonlyAtom)
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          使用 userReadonlyAtom(userId) 来获取单个用户数据。当 usersAtom 中的数据更新时，这个组件会自动重新渲染。
        </Typography>
        <Box sx={{ mb: 2 }}>
          <TextField
            label="选择用户 ID"
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            helperText="输入用户 ID 来查看对应的用户"
            sx={{ minWidth: 200 }}
          />
        </Box>
        <SingleUserDisplay userId={selectedUserId} />
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* 多个用户显示 - 使用 usersByIdsAtomFamily */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          多个用户显示 (usersByIdsAtomFamily)
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          使用 usersByIdsAtomFamily([id1, id2, ...]) 来获取多个用户数据。传入一个 ID 数组，返回对应的用户数组。
        </Typography>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="选择用户 IDs (用逗号分隔)"
            value={selectedUserIds.join(', ')}
            onChange={(e) => {
              const ids = e.target.value
                .split(',')
                .map((id) => id.trim())
                .filter((id) => id)
              setSelectedUserIds(ids)
            }}
            helperText="例如: 1, 2, 3"
          />
        </Box>
        <MultipleUsersDisplay userIds={selectedUserIds} />
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* 所有用户显示 - 使用 usersAtom */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          所有用户显示 (usersAtom)
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          直接使用 usersAtom 来获取所有用户数据。这是一个对象，键是用户 ID，值是用户对象。
        </Typography>
        <AllUsersDisplay />
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Atom 说明 */}
      <Paper sx={{ p: 3, bgcolor: 'background.default' }}>
        <Typography variant="h6" gutterBottom>
          Atom 说明
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>
            <Typography variant="body2">
              <strong>usersAtom</strong>: 存储所有用户的映射对象，键是 resourceId，值是用户对象。可以通过 setUsers 来更新。
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <strong>userReadonlyAtom</strong>: 根据 userId 动态创建 atom，用于获取单个用户。当 usersAtom 中的数据更新时，会自动反映到对应的 userReadonlyAtom。
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <strong>usersByIdsAtomFamily</strong>: 根据多个 userId 数组创建 atom，返回对应的用户数组。用于批量获取用户数据。
            </Typography>
          </li>
        </Box>
        <Typography variant="body2" sx={{ mt: 2 }}>
          <strong>使用方式：</strong>
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>
            <Typography variant="body2" component="code" sx={{ fontFamily: 'monospace' }}>
              const [user] = useAtom(userReadonlyAtom(userId))
            </Typography>
          </li>
          <li>
            <Typography variant="body2" component="code" sx={{ fontFamily: 'monospace' }}>
              const users = useAtomValue(usersByIdsAtomFamily([id1, id2]))
            </Typography>
          </li>
          <li>
            <Typography variant="body2" component="code" sx={{ fontFamily: 'monospace' }}>
              const [users, setUsers] = useAtom(usersAtom)
            </Typography>
          </li>
        </Box>
      </Paper>
    </Container>
  )
}
